import { useState } from "react";
import {
  Caso,
  Holding,
  Cliente,
  Propiedad,
  CasoPropiedad,
  Poder,
  Estudio,
  Solicitud,
  Gasto,
  Usuario,
  EntradaBitacora,
  Documento,
  EstadoCaso,
  EstadoSolicitud,
  TipoGasto,
  EventoEstado,
  Reclamo,
  Cobro,
  Tesoreria,
  AnalisisEstudio,
} from "../../types";
import { Badge, getEstadoCasoBadgeVariant, getEstadoReclamoBadgeVariant, getEstadoCobroBadgeVariant, getEstadoTesoreriaBadgeVariant } from "../common/Badge";
import { Modal } from "../common/Modal";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { BitacoraSection } from "./BitacoraSection";
import { DocumentosSection } from "./DocumentosSection";
import { getTransicionesDisponibles, ESTADO_SOLICITUD_LABELS, FLUJO_PRINCIPAL } from "../../data/transicionesSolicitud";

type Tab = "info" | "roles" | "gastos" | "bitacora" | "documentos" | "progreso";

interface CasoDetailProps {
  caso: Caso;
  holding: Holding;
  clientes: Cliente[];
  propiedades: Propiedad[];
  casoPropiedades: CasoPropiedad[];
  poderes: Poder[];
  estudios: Estudio[];
  solicitudes: Solicitud[];
  gastos: Gasto[];
  usuarios: Usuario[];
  bitacora: EntradaBitacora[];
  documentos: Documento[];
  usuarioActual: Usuario;
  onCasoUpdate: (caso: Caso) => void;
  onCasoPropiedadCreate: (data: Omit<CasoPropiedad, "id">) => void;
  onCasoPropiedadDelete: (id: number) => void;
  onEstudioCreate: (data: Omit<Estudio, "id">) => void;
  onEstudioUpdate: (estudio: Estudio) => void;
  onSolicitudCreate: (data: Omit<Solicitud, "id">) => void;
  onSolicitudUpdate: (solicitud: Solicitud) => void;
  onGastoCreate: (data: Omit<Gasto, "id">) => void;
  onGastoUpdate: (gasto: Gasto) => void;
  onGastoDelete: (id: number) => void;
  onBitacoraCreate: (entrada: Omit<EntradaBitacora, "id" | "fechaCreacion">) => void;
  onDocumentoCreate: (documento: Omit<Documento, "id" | "fechaSubida">) => void;
  onDocumentoDelete: (id: number) => void;
  eventosEstado: EventoEstado[];
  reclamos: Reclamo[];
  cobros: Cobro[];
  tesoreriaItems: Tesoreria[];
  onCambioEstadoSolicitud: (caso: Caso, nuevoEstado: Caso["estado"], comentario: string, documentoId?: number) => void;
  analisisEstudios: AnalisisEstudio[];
}

export function CasoDetail({
  caso,
  holding,
  clientes,
  propiedades,
  casoPropiedades,
  poderes,
  estudios,
  solicitudes,
  gastos,
  usuarios,
  bitacora,
  documentos,
  usuarioActual,
  onCasoUpdate,
  onCasoPropiedadCreate,
  onCasoPropiedadDelete,
  onEstudioCreate,
  onEstudioUpdate,
  onSolicitudCreate,
  onSolicitudUpdate,
  onGastoCreate,
  onGastoUpdate,
  onGastoDelete,
  onBitacoraCreate,
  onDocumentoCreate,
  onDocumentoDelete,
  eventosEstado,
  reclamos,
  cobros,
  tesoreriaItems,
  onCambioEstadoSolicitud,
  analisisEstudios,
}: CasoDetailProps) {
  const [activeTab, setActiveTab] = useState<Tab>("info");
  const [showAddPropiedad, setShowAddPropiedad] = useState(false);
  const [selectedPropiedadId, setSelectedPropiedadId] = useState<number>(0);
  const [expandedPropiedad, setExpandedPropiedad] = useState<number | null>(null);
  const [showSolicitudForm, setShowSolicitudForm] = useState<number | null>(null);
  const [showGastoForm, setShowGastoForm] = useState(false);
  const [gastoToDelete, setGastoToDelete] = useState<Gasto | null>(null);
  const [propiedadToRemove, setPropiedadToRemove] = useState<CasoPropiedad | null>(null);
  const [showPoderConfirm, setShowPoderConfirm] = useState(false);
  const [showDesistirDialog, setShowDesistirDialog] = useState(false);
  const [motivoDesistimientoInput, setMotivoDesistimientoInput] = useState("");
  const [showTransicionModal, setShowTransicionModal] = useState(false);
  const [transicionDestino, setTransicionDestino] = useState<EstadoCaso | null>(null);
  const [transicionComentario, setTransicionComentario] = useState("");

  // Gasto form state
  const [gastoTipo, setGastoTipo] = useState<TipoGasto>("tramite");
  const [gastoDescripcion, setGastoDescripcion] = useState("");
  const [gastoMonto, setGastoMonto] = useState("");
  const [gastoFecha, setGastoFecha] = useState(new Date().toISOString().split("T")[0]);
  const [gastoResponsableId, setGastoResponsableId] = useState(usuarioActual.id);

  // (Estudio form state removed — estudios now live in Analysis & Estudios tab)

  // Solicitud form state
  const [solicitudPoderId, setSolicitudPoderId] = useState<number>(0);
  const [solicitudNumero, setSolicitudNumero] = useState("");
  const [solicitudObservaciones, setSolicitudObservaciones] = useState("");

  const getUsuarioNombre = (id: number) =>
    usuarios.find((u) => u.id === id)?.nombre || "-";

  const formatMoney = (value?: number) =>
    value ? `$${value.toLocaleString("es-CL")}` : "-";

  const formatDate = (date?: string) =>
    date ? new Date(date).toLocaleDateString("es-CL") : "-";

  // Get holding clientes and their propiedades
  const holdingClientes = clientes.filter((c) => c.holdingId === holding.id);
  const holdingClienteIds = holdingClientes.map((c) => c.id);
  const holdingPropiedades = propiedades.filter((p) => holdingClienteIds.includes(p.clienteId));

  // Get linked propiedades for this caso
  const linkedPropiedadIds = casoPropiedades.map((cp) => cp.propiedadId);
  const linkedPropiedades = propiedades.filter((p) => linkedPropiedadIds.includes(p.id));

  // Available propiedades (not yet linked)
  const availablePropiedades = holdingPropiedades.filter(
    (p) => !linkedPropiedadIds.includes(p.id)
  );

  // arquitectos list removed (estudios now in Analysis tab)

  // Transiciones disponibles desde estado actual
  const transicionesDisponibles = getTransicionesDisponibles(caso.estado);
  const currentFlowIdx = FLUJO_PRINCIPAL.indexOf(caso.estado);

  const casoDocumentos = documentos.filter((d) => d.casoId === caso.id);
  const hasPoder = casoDocumentos.some((d) => d.clasificacion === "Poder");

  const handleTransicion = () => {
    if (!transicionDestino || !transicionComentario.trim()) return;

    // Gate: en_preparacion → confirmacion_presentacion: warn about missing docs
    if (caso.estado === "en_preparacion" && transicionDestino === "confirmacion_presentacion" && !hasPoder) {
      setShowPoderConfirm(true);
      return;
    }

    onCambioEstadoSolicitud(caso, transicionDestino, transicionComentario.trim());
    setShowTransicionModal(false);
    setTransicionDestino(null);
    setTransicionComentario("");
  };

  const handleAdvanceSinPoder = () => {
    setShowPoderConfirm(false);
    if (transicionDestino) {
      onCambioEstadoSolicitud(caso, transicionDestino, transicionComentario.trim());
      setShowTransicionModal(false);
      setTransicionDestino(null);
      setTransicionComentario("");
    }
  };

  const handleDesistir = () => {
    if (motivoDesistimientoInput.trim()) {
      onCambioEstadoSolicitud(caso, "desistido", motivoDesistimientoInput.trim());
      setShowDesistirDialog(false);
      setMotivoDesistimientoInput("");
    }
  };

  const isTerminal = ["ganado", "parcialmente_ganado", "perdido", "cobro_facturacion", "tesoreria", "reclamo", "desistido"].includes(caso.estado);
  const canDesistir = !isTerminal;

  const handleAddPropiedad = () => {
    if (selectedPropiedadId > 0) {
      onCasoPropiedadCreate({
        casoId: caso.id,
        propiedadId: selectedPropiedadId,
      });
      setSelectedPropiedadId(0);
      setShowAddPropiedad(false);
    }
  };

  const handleCreateSolicitud = (propiedadId: number) => {
    onSolicitudCreate({
      casoId: caso.id,
      propiedadId,
      poderId: solicitudPoderId || undefined,
      estado: "borrador",
      numeroSolicitud: solicitudNumero || undefined,
      observaciones: solicitudObservaciones || undefined,
    });
    setShowSolicitudForm(null);
    setSolicitudNumero("");
    setSolicitudObservaciones("");
    setSolicitudPoderId(0);
  };

  const handleCreateGasto = (e: React.FormEvent) => {
    e.preventDefault();
    if (gastoDescripcion.trim() && gastoMonto) {
      onGastoCreate({
        casoId: caso.id,
        tipo: gastoTipo,
        descripcion: gastoDescripcion.trim(),
        monto: Number(gastoMonto),
        fecha: gastoFecha,
        responsableId: gastoResponsableId,
      });
      setGastoTipo("tramite");
      setGastoDescripcion("");
      setGastoMonto("");
      setGastoFecha(new Date().toISOString().split("T")[0]);
      setGastoResponsableId(usuarioActual.id);
      setShowGastoForm(false);
    }
  };

  const getEstudioForPropiedad = (propiedadId: number) =>
    estudios.find((e) => e.propiedadId === propiedadId);

  const getSolicitudForPropiedad = (propiedadId: number) =>
    solicitudes.find((s) => s.propiedadId === propiedadId);

  const getPoderesForCliente = (clienteId: number) =>
    poderes.filter((p) => p.clienteId === clienteId);

  const getEstadoSolicitudLabel = (estado: EstadoSolicitud) => {
    switch (estado) {
      case "borrador": return "Borrador";
      case "enviada": return "Enviada";
      case "en_revision": return "En Revision";
      case "resuelta": return "Resuelta";
      case "rechazada": return "Rechazada";
    }
  };

  const totalGastos = gastos.reduce((sum, g) => sum + g.monto, 0);

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: "info", label: "Informacion" },
    { id: "progreso", label: "Progreso", count: eventosEstado.length },
    { id: "roles", label: "ROLes", count: linkedPropiedades.length },
    { id: "gastos", label: "Gastos", count: gastos.length },
    { id: "bitacora", label: "Bitacora", count: bitacora.length },
    { id: "documentos", label: "Documentos", count: documentos.length },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-slate-800">{caso.titulo}</h2>
            <Badge variant={getEstadoCasoBadgeVariant(caso.estado)}>
              {caso.estado.replace("_", " ")}
            </Badge>
            {caso.resultado && (
              <Badge variant={caso.resultado === "ganado" ? "success" : "error"}>
                {caso.resultado}
              </Badge>
            )}
          </div>
          {canDesistir && (
            <button
              onClick={() => setShowDesistirDialog(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            >
              Desistir
            </button>
          )}
        </div>
        <p className="text-slate-600">{holding.nombre}</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 px-6 bg-white">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-600 hover:text-slate-800"
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-1.5 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
        {/* ===================== INFO TAB ===================== */}
        {activeTab === "info" && (
          <div className="space-y-6">
            {/* Detalles del caso */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">
                Detalles del Caso
              </h3>

              {/* Motivo de desistimiento (solo si esta desistido) */}
              {caso.estado === "desistido" && caso.motivoDesistimiento && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-700 mb-1">Desistido por:</p>
                  <p className="text-sm text-red-600">{caso.motivoDesistimiento}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Tipo de Presentacion</p>
                  <p className="text-xs text-slate-500">
                    {caso.categoriaPresentacion === "actualizacion_modificacion"
                      ? "Actualizacion y/o Modificacion"
                      : "Rebajas, beneficios y exenciones"}
                  </p>
                  <p className="text-sm font-medium text-slate-800">
                    {caso.tipoPresentacion}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Lugar de Presentacion</p>
                  <p className="text-sm font-medium text-slate-800">
                    {caso.lugarPresentacion.replace("_", " ")}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Usuario</p>
                  <p className="text-sm font-medium text-slate-800">
                    {caso.usuario}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Cliente</p>
                  <p className="text-sm font-medium text-slate-800">
                    {holding.nombre}
                  </p>
                </div>
              </div>
            </div>

            {/* Equipo */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">
                Equipo Asignado
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Ejecutivo</p>
                  <p className="text-sm font-medium text-slate-800">
                    {getUsuarioNombre(caso.ejecutivoId)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Abogado</p>
                  <p className="text-sm font-medium text-slate-800">
                    {caso.abogadoId ? getUsuarioNombre(caso.abogadoId) : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Arquitecto</p>
                  <p className="text-sm font-medium text-slate-800">
                    {caso.arquitectoId ? getUsuarioNombre(caso.arquitectoId) : "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Origen (estudio) */}
            {caso.analisisEstudioId && (() => {
              const estudioOrigen = analisisEstudios.find((a) => a.id === caso.analisisEstudioId);
              if (!estudioOrigen) return null;
              const estadoVariant: Record<string, "neutral" | "info" | "success" | "error"> = {
                creado: "neutral",
                en_estudio: "info",
                completado: "success",
                desistido: "error",
              };
              return (
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Origen</h3>
                  <div className="flex items-center gap-3">
                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800">
                        Creado desde estudio: {estudioOrigen.titulo}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {new Date(estudioOrigen.fechaCreacion).toLocaleDateString("es-CL")}
                      </p>
                    </div>
                    <Badge variant={estadoVariant[estudioOrigen.estado] || "neutral"}>
                      {estudioOrigen.estado.replace("_", " ")}
                    </Badge>
                  </div>
                </div>
              );
            })()}

            {/* Valores financieros */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">
                Valores Estimados
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700">Rebaja Esperada</p>
                  <p className="text-xl font-bold text-green-800">
                    {formatMoney(caso.rebajaEsperada)}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">Honorario</p>
                  <p className="text-xl font-bold text-blue-800">
                    {formatMoney(caso.honorario)}
                  </p>
                </div>
              </div>
            </div>

            {/* Fechas */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">
                Fechas
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Fecha Creacion</p>
                  <p className="font-medium text-slate-800">
                    {formatDate(caso.fechaCreacion)}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Ultima Actualizacion</p>
                  <p className="font-medium text-slate-800">
                    {formatDate(caso.fechaActualizacion)}
                  </p>
                </div>
                {caso.fechaPresentacion && (
                  <div>
                    <p className="text-slate-500">Fecha Presentacion</p>
                    <p className="font-medium text-slate-800">
                      {formatDate(caso.fechaPresentacion)}
                    </p>
                  </div>
                )}
                {caso.fechaResolucion && (
                  <div>
                    <p className="text-slate-500">Fecha Resolucion</p>
                    <p className="font-medium text-slate-800">
                      {formatDate(caso.fechaResolucion)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===================== PROGRESO TAB ===================== */}
        {activeTab === "progreso" && (
          <div className="space-y-4">
            {/* Workflow Flow */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-sm font-semibold text-slate-700">
                  Progreso de la Solicitud
                </h3>
                {caso.poderPendiente && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                    Poder Pendiente
                  </span>
                )}
              </div>

              {/* Flow principal */}
              <div className="flex items-center gap-1 flex-wrap">
                {FLUJO_PRINCIPAL.map((estado, idx) => {
                  const isPast = currentFlowIdx >= 0 && idx < currentFlowIdx;
                  const isCurrent = estado === caso.estado;
                  return (
                    <div key={estado} className="flex items-center gap-1">
                      {idx > 0 && (
                        <div className={`w-4 h-0.5 ${isPast ? "bg-green-400" : "bg-slate-200"}`} />
                      )}
                      <div
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          isCurrent
                            ? "bg-blue-100 text-blue-700 ring-1 ring-blue-400"
                            : isPast
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {ESTADO_SOLICITUD_LABELS[estado]}
                      </div>
                    </div>
                  );
                })}

                {/* Post-resolucion states */}
                {!FLUJO_PRINCIPAL.includes(caso.estado) && caso.estado !== "desistido" && (
                  <>
                    <div className="w-4 h-0.5 bg-green-400" />
                    <div className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 ring-1 ring-blue-400">
                      {ESTADO_SOLICITUD_LABELS[caso.estado] || caso.estado.replace("_", " ")}
                    </div>
                  </>
                )}
                {caso.estado === "desistido" && (
                  <>
                    <div className="w-4 h-0.5 bg-red-300" />
                    <div className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 ring-1 ring-red-400">
                      Desistido
                    </div>
                  </>
                )}
              </div>

              {/* Resultado badge */}
              {caso.resultado && (
                <div className={`mt-3 p-2 rounded-lg text-sm ${
                  caso.resultado === "ganado" ? "bg-green-50 text-green-700" :
                  caso.resultado === "parcialmente_ganado" ? "bg-yellow-50 text-yellow-700" :
                  "bg-red-50 text-red-700"
                }`}>
                  Resultado: <strong>{caso.resultado === "parcialmente_ganado" ? "Parcialmente Ganado" : caso.resultado === "ganado" ? "Ganado" : "Perdido"}</strong>
                  {caso.fechaResolucion && (
                    <span className="ml-2 opacity-70">({formatDate(caso.fechaResolucion)})</span>
                  )}
                </div>
              )}

              {/* Entidades hijas */}
              {(reclamos.length > 0 || cobros.length > 0 || tesoreriaItems.length > 0) && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  {cobros.map((c) => (
                    <Badge key={`c-${c.id}`} variant={getEstadoCobroBadgeVariant(c.estado)}>
                      Cobro: {c.estado.replace("_", " ")}
                    </Badge>
                  ))}
                  {tesoreriaItems.map((t) => (
                    <Badge key={`t-${t.id}`} variant={getEstadoTesoreriaBadgeVariant(t.estado)}>
                      Tesoreria: {t.estado.replace("_", " ")}
                    </Badge>
                  ))}
                  {reclamos.map((r) => (
                    <Badge key={`r-${r.id}`} variant={getEstadoReclamoBadgeVariant(r.estado)}>
                      Reclamo: {r.estado.replace("_", " ")}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Transition buttons */}
              {transicionesDisponibles.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {transicionesDisponibles.map((t) => (
                    <button
                      key={t.hacia}
                      onClick={() => {
                        if (t.hacia === "desistido") {
                          setShowDesistirDialog(true);
                        } else {
                          setTransicionDestino(t.hacia);
                          setShowTransicionModal(true);
                        }
                      }}
                      className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                        t.hacia === "desistido"
                          ? "bg-red-100 text-red-700 hover:bg-red-200"
                          : t.hacia === "ganado"
                          ? "bg-green-600 text-white hover:bg-green-700"
                          : t.hacia === "perdido"
                          ? "bg-red-600 text-white hover:bg-red-700"
                          : t.hacia === "parcialmente_ganado"
                          ? "bg-yellow-600 text-white hover:bg-yellow-700"
                          : t.hacia === "reclamo"
                          ? "bg-orange-600 text-white hover:bg-orange-700"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Transition modal */}
            {showTransicionModal && transicionDestino && (
              <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
                <p className="text-sm font-medium text-yellow-800 mb-2">
                  Cambiar estado a: <strong>{ESTADO_SOLICITUD_LABELS[transicionDestino] || transicionDestino}</strong>
                </p>
                {getTransicionesDisponibles(caso.estado).find((t) => t.hacia === transicionDestino)?.creaEntidad && (
                  <p className="text-xs text-yellow-600 mb-2">
                    Se creara automaticamente: {getTransicionesDisponibles(caso.estado).find((t) => t.hacia === transicionDestino)?.creaEntidad?.join(", ")}
                  </p>
                )}
                <textarea
                  value={transicionComentario}
                  onChange={(e) => setTransicionComentario(e.target.value)}
                  placeholder="Comentario obligatorio..."
                  rows={2}
                  className="w-full px-3 py-1.5 border border-yellow-300 rounded-lg text-sm mb-2"
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => {
                      setShowTransicionModal(false);
                      setTransicionDestino(null);
                      setTransicionComentario("");
                    }}
                    className="text-xs px-3 py-1.5 text-slate-600"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleTransicion}
                    disabled={!transicionComentario.trim()}
                    className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg disabled:bg-slate-300"
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            )}

            <h3 className="text-sm font-semibold text-slate-700">Timeline de la Solicitud</h3>
            {eventosEstado.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-8 bg-white rounded-lg border border-slate-200">
                No hay eventos de estado registrados. Los eventos se crean automaticamente al cambiar el estado de la solicitud.
              </div>
            ) : (
              <div className="space-y-3">
                {[...eventosEstado]
                  .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())
                  .map((evento) => (
                    <div key={evento.id} className="bg-white rounded-lg border border-slate-200 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={getEstadoCasoBadgeVariant(evento.estadoAnterior)}>
                          {ESTADO_SOLICITUD_LABELS[evento.estadoAnterior as EstadoCaso] || evento.estadoAnterior.replace("_", " ")}
                        </Badge>
                        <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <Badge variant={getEstadoCasoBadgeVariant(evento.estadoNuevo)}>
                          {ESTADO_SOLICITUD_LABELS[evento.estadoNuevo as EstadoCaso] || evento.estadoNuevo.replace("_", " ")}
                        </Badge>
                        <span className="text-xs text-slate-400 ml-auto">
                          {new Date(evento.fecha).toLocaleDateString("es-CL")}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">{evento.comentario}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Por {getUsuarioNombre(evento.usuarioId)}
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* ===================== ROLES TAB ===================== */}
        {activeTab === "roles" && (
          <div className="space-y-4">
            {/* Add property button */}
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold text-slate-700">
                Propiedades vinculadas ({linkedPropiedades.length})
              </h3>
              <button
                onClick={() => setShowAddPropiedad(true)}
                disabled={availablePropiedades.length === 0}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                + Agregar Propiedad
              </button>
            </div>

            {/* Linked properties list */}
            {linkedPropiedades.length > 0 ? (
              <div className="space-y-3">
                {linkedPropiedades.map((prop) => {
                  const cp = casoPropiedades.find((cp) => cp.propiedadId === prop.id);
                  const solicitud = getSolicitudForPropiedad(prop.id);
                  const propCliente = clientes.find((c) => c.id === prop.clienteId);
                  const propPoderes = propCliente ? getPoderesForCliente(propCliente.id) : [];
                  const isExpanded = expandedPropiedad === prop.id;

                  return (
                    <div
                      key={prop.id}
                      className="bg-white rounded-lg border border-slate-200"
                    >
                      {/* Property header */}
                      <div
                        className="p-4 cursor-pointer flex justify-between items-center"
                        onClick={() =>
                          setExpandedPropiedad(isExpanded ? null : prop.id)
                        }
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-slate-800">
                              ROL {prop.rol}
                            </span>
                            <span className="text-sm text-slate-500">
                              {prop.comuna}
                            </span>
                            {prop.tipoTenencia === "leasing" && (
                              <span className="text-sm text-yellow-600">
                                (Leasing)
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            {solicitud && (
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full ${
                                  solicitud.estado === "resuelta"
                                    ? "bg-green-100 text-green-700"
                                    : solicitud.estado === "rechazada"
                                    ? "bg-red-100 text-red-700"
                                    : solicitud.estado === "enviada" || solicitud.estado === "en_revision"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : "bg-slate-100 text-slate-600"
                                }`}
                              >
                                Solicitud: {getEstadoSolicitudLabel(solicitud.estado)}
                              </span>
                            )}
                            {!solicitud && (
                              <span className="text-xs text-slate-400">
                                Sin solicitud
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (cp) setPropiedadToRemove(cp);
                            }}
                            className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                            title="Desvincular"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                          <svg
                            className={`w-5 h-5 text-slate-400 transition-transform ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Expanded section */}
                      {isExpanded && (
                        <div className="border-t border-slate-200 p-4 space-y-4">
                          {/* Solicitud section */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="text-sm font-semibold text-slate-700">
                                Solicitud
                              </h4>
                              {!solicitud && (
                                <button
                                  onClick={() => setShowSolicitudForm(prop.id)}
                                  className="text-xs px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                  + Crear Solicitud
                                </button>
                              )}
                            </div>
                            {solicitud ? (
                              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                                <div className="grid grid-cols-3 gap-3 text-sm">
                                  <div>
                                    <p className="text-slate-500 text-xs">Estado</p>
                                    <p className="font-medium">
                                      {getEstadoSolicitudLabel(solicitud.estado)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500 text-xs">Numero</p>
                                    <p className="font-medium">
                                      {solicitud.numeroSolicitud || "-"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500 text-xs">Poder</p>
                                    <p className="font-medium">
                                      {solicitud.poderId
                                        ? poderes.find((p) => p.id === solicitud.poderId)
                                            ?.tipoDocumento || "Si"
                                        : "Sin poder"}
                                    </p>
                                  </div>
                                </div>
                                {solicitud.observaciones && (
                                  <p className="text-sm text-slate-600">
                                    {solicitud.observaciones}
                                  </p>
                                )}
                                <div className="text-xs text-slate-500">
                                  {solicitud.fechaEnvio && `Envio: ${formatDate(solicitud.fechaEnvio)}`}
                                  {solicitud.fechaRespuesta &&
                                    ` | Respuesta: ${formatDate(solicitud.fechaRespuesta)}`}
                                </div>
                                {solicitud.estado === "borrador" && (
                                  <button
                                    onClick={() =>
                                      onSolicitudUpdate({
                                        ...solicitud,
                                        estado: "enviada",
                                        fechaEnvio: new Date().toISOString().split("T")[0],
                                      })
                                    }
                                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                  >
                                    Marcar como Enviada
                                  </button>
                                )}
                                {solicitud.estado === "enviada" && (
                                  <button
                                    onClick={() =>
                                      onSolicitudUpdate({
                                        ...solicitud,
                                        estado: "en_revision",
                                      })
                                    }
                                    className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                                  >
                                    En Revision
                                  </button>
                                )}
                                {solicitud.estado === "en_revision" && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() =>
                                        onSolicitudUpdate({
                                          ...solicitud,
                                          estado: "resuelta",
                                          fechaRespuesta: new Date().toISOString().split("T")[0],
                                        })
                                      }
                                      className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                    >
                                      Resuelta
                                    </button>
                                    <button
                                      onClick={() =>
                                        onSolicitudUpdate({
                                          ...solicitud,
                                          estado: "rechazada",
                                          fechaRespuesta: new Date().toISOString().split("T")[0],
                                        })
                                      }
                                      className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                    >
                                      Rechazada
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : showSolicitudForm === prop.id ? (
                              <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-slate-600 mb-1">
                                    Poder
                                  </label>
                                  <select
                                    value={solicitudPoderId}
                                    onChange={(e) =>
                                      setSolicitudPoderId(Number(e.target.value))
                                    }
                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value={0}>Sin poder</option>
                                    {propPoderes
                                      .filter((p) => p.estado === "vigente")
                                      .map((p) => (
                                        <option key={p.id} value={p.id}>
                                          {p.tipoDocumento} (
                                          {formatDate(p.fechaOtorgamiento)})
                                        </option>
                                      ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-slate-600 mb-1">
                                    Numero de Solicitud
                                  </label>
                                  <input
                                    type="text"
                                    value={solicitudNumero}
                                    onChange={(e) =>
                                      setSolicitudNumero(e.target.value)
                                    }
                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-slate-600 mb-1">
                                    Observaciones
                                  </label>
                                  <textarea
                                    value={solicitudObservaciones}
                                    onChange={(e) =>
                                      setSolicitudObservaciones(e.target.value)
                                    }
                                    rows={2}
                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                  />
                                </div>
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => setShowSolicitudForm(null)}
                                    className="text-xs px-3 py-1 text-slate-600 bg-slate-200 rounded-lg hover:bg-slate-300 transition-colors"
                                  >
                                    Cancelar
                                  </button>
                                  <button
                                    onClick={() => handleCreateSolicitud(prop.id)}
                                    className="text-xs px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                  >
                                    Crear Solicitud
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400">
                                No hay solicitud creada para esta propiedad.
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
                No hay propiedades vinculadas a este caso
              </div>
            )}

            {/* Add property modal */}
            <Modal
              isOpen={showAddPropiedad}
              onClose={() => setShowAddPropiedad(false)}
              title="Agregar Propiedad al Caso"
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Seleccionar Propiedad
                  </label>
                  <select
                    value={selectedPropiedadId}
                    onChange={(e) =>
                      setSelectedPropiedadId(Number(e.target.value))
                    }
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={0}>Seleccionar...</option>
                    {availablePropiedades.map((p) => (
                      <option key={p.id} value={p.id}>
                        ROL {p.rol} - {p.comuna}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowAddPropiedad(false)}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAddPropiedad}
                    disabled={selectedPropiedadId === 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                  >
                    Agregar
                  </button>
                </div>
              </div>
            </Modal>

            {/* Confirm remove property */}
            <ConfirmDialog
              isOpen={propiedadToRemove !== null}
              onClose={() => setPropiedadToRemove(null)}
              onConfirm={() => {
                if (propiedadToRemove) {
                  onCasoPropiedadDelete(propiedadToRemove.id);
                }
              }}
              title="Desvincular Propiedad"
              message="Esta seguro que desea desvincular esta propiedad del caso?"
              confirmText="Desvincular"
            />
          </div>
        )}

        {/* ===================== GASTOS TAB ===================== */}
        {activeTab === "gastos" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold text-slate-700">
                Gastos ({gastos.length})
              </h3>
              <button
                onClick={() => setShowGastoForm(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                + Agregar Gasto
              </button>
            </div>

            {showGastoForm && (
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">
                  Nuevo Gasto
                </h4>
                <form onSubmit={handleCreateGasto} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Tipo
                      </label>
                      <select
                        value={gastoTipo}
                        onChange={(e) =>
                          setGastoTipo(e.target.value as TipoGasto)
                        }
                        className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="tramite">Tramite</option>
                        <option value="traslado">Traslado</option>
                        <option value="viaje">Viaje</option>
                        <option value="otro">Otro</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Monto ($) *
                      </label>
                      <input
                        type="number"
                        value={gastoMonto}
                        onChange={(e) => setGastoMonto(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Descripcion *
                    </label>
                    <input
                      type="text"
                      value={gastoDescripcion}
                      onChange={(e) => setGastoDescripcion(e.target.value)}
                      className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Fecha
                      </label>
                      <input
                        type="date"
                        value={gastoFecha}
                        onChange={(e) => setGastoFecha(e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Responsable
                      </label>
                      <select
                        value={gastoResponsableId}
                        onChange={(e) =>
                          setGastoResponsableId(Number(e.target.value))
                        }
                        className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {usuarios.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.nombre}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowGastoForm(false)}
                      className="px-3 py-1.5 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={!gastoDescripcion.trim() || !gastoMonto}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-300"
                    >
                      Agregar
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Gastos table */}
            {gastos.length > 0 ? (
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-3 text-left font-medium text-slate-600">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">
                        Descripcion
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-slate-600">
                        Monto
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-slate-600">
                        Responsable
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-slate-600">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {gastos.map((gasto) => (
                      <tr key={gasto.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className="capitalize">{gasto.tipo}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          {gasto.descripcion}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {formatMoney(gasto.monto)}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {formatDate(gasto.fecha)}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {getUsuarioNombre(gasto.responsableId)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setGastoToDelete(gasto)}
                            className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                            title="Eliminar"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 border-t border-slate-200">
                      <td
                        colSpan={2}
                        className="px-4 py-3 font-semibold text-slate-700"
                      >
                        Total
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-slate-800">
                        {formatMoney(totalGastos)}
                      </td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
                No hay gastos registrados para este caso
              </div>
            )}

            <ConfirmDialog
              isOpen={gastoToDelete !== null}
              onClose={() => setGastoToDelete(null)}
              onConfirm={() => {
                if (gastoToDelete) {
                  onGastoDelete(gastoToDelete.id);
                }
              }}
              title="Eliminar Gasto"
              message={`Esta seguro que desea eliminar el gasto "${gastoToDelete?.descripcion}"?`}
              confirmText="Eliminar"
            />
          </div>
        )}

        {/* ===================== BITACORA TAB ===================== */}
        {activeTab === "bitacora" && (
          <BitacoraSection
            casoId={caso.id}
            bitacora={bitacora}
            usuarios={usuarios}
            usuarioActual={usuarioActual}
            onEntradaCreate={onBitacoraCreate}
          />
        )}

        {/* ===================== DOCUMENTOS TAB ===================== */}
        {activeTab === "documentos" && (
          <DocumentosSection
            casoId={caso.id}
            documentos={documentos}
            usuarios={usuarios}
            usuarioActual={usuarioActual}
            onDocumentoCreate={onDocumentoCreate}
            onDocumentoDelete={onDocumentoDelete}
          />
        )}
      </div>

      {/* Poder Pendiente Confirmation */}
      <ConfirmDialog
        isOpen={showPoderConfirm}
        onClose={() => setShowPoderConfirm(false)}
        onConfirm={handleAdvanceSinPoder}
        title="Poder no encontrado"
        message="Este caso no tiene un documento de Poder subido. ¿Seguro que quieres avanzar a En Solicitud sin el Poder? El caso quedará marcado como 'Poder Pendiente'."
        confirmText="Avanzar sin Poder"
      />

      {/* Desistir Confirmation Modal */}
      <Modal
        isOpen={showDesistirDialog}
        onClose={() => {
          setShowDesistirDialog(false);
          setMotivoDesistimientoInput("");
        }}
        title="¿Estas seguro que quieres desistir el caso?"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Esta accion cambiara el estado del caso a "Desistido". Por favor, ingrese la razon del desistimiento.
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Razon del desistimiento *
            </label>
            <textarea
              value={motivoDesistimientoInput}
              onChange={(e) => setMotivoDesistimientoInput(e.target.value)}
              rows={3}
              placeholder="Ingrese la razon por la cual se desiste del caso..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => {
                setShowDesistirDialog(false);
                setMotivoDesistimientoInput("");
              }}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleDesistir}
              disabled={!motivoDesistimientoInput.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              Desistir
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
